import { fetchWithTimeout, fetchWithAuth, FetchWithTimeoutOptions } from './fetch-utils';
import { deleteCacheEntry, getCacheEntry, putCacheEntry, type CacheRef } from '@/db/services/http_cache_service';

export interface ConditionalGetResult {
  response: Response;
  notModified: boolean;
  eTag: string | null;
  lastModified: string | null;
}

export interface FetchFromApiOptions extends FetchWithTimeoutOptions {
  /** ETag to send as If-None-Match (a per-record validator, e.g. pullDocument's) */
  storedETag?: string;
  /** Last-Modified to send as If-Modified-Since */
  storedLastModified?: string;
  /** If true, uses fetchWithAuth (handles 401 → logout). Default: true */
  authenticated?: boolean;
}

/**
 * Fetch from the Mon Ami API with HTTP Conditional GET support.
 *
 * Attaches If-None-Match / If-Modified-Since from the validators given,
 * detects a 304, and hands back the response's own ETag / Last-Modified.
 * It stores nothing: callers that cache validators use fetchWithCache, or
 * keep them on the record they belong to.
 */
export async function fetchFromApi(
  url: string | URL,
  options: FetchFromApiOptions = {},
): Promise<ConditionalGetResult> {
  const { storedETag, storedLastModified, authenticated = true, ...fetchOptions } = options;

  const headers: Record<string, string> = {
    ...(fetchOptions.headers as Record<string, string> || {}),
  };
  if (storedETag) headers['If-None-Match'] = storedETag;
  if (storedLastModified) headers['If-Modified-Since'] = storedLastModified;

  // Make the request
  const response = authenticated
    ? await fetchWithAuth(url, { ...fetchOptions, headers })
    : await fetchWithTimeout(url, { ...fetchOptions, headers });

  const notModified = response.status === 304;
  return { response, notModified, eTag: response.headers.get('ETag'), lastModified: response.headers.get('Last-Modified') };
}

export interface CachedFetchResult extends ConditionalGetResult {
  /**
   * Record the validators of a 200 so the next fetch can 304. Call it once the
   * body has been stored; until then a retry fetches the resource in full.
   */
  commit: () => Promise<void>;
}

/**
 * Conditional GET for a single resource whose validators live in the httpCache
 * table (help documents, one template, an organization's programs). Paginated
 * collections use lib/paged-collection, which keeps a manifest per page.
 */
export interface CachedFetchOptions extends Omit<FetchFromApiOptions, 'storedETag' | 'storedLastModified'> {
  /**
   * Is the body this validator vouches for still on the device? A 304 hands
   * back nothing, so a validator whose row was removed locally (a pruned
   * template, a cleared help document) must not be sent; when this answers
   * false the stored entry is dropped and the resource is fetched in full.
   */
  haveBody?: () => Promise<boolean>;
}

export const fetchWithCache = withCache(fetchFromApi);

/** The caching layer over any conditional-GET transport; tests wrap a stubbed fetchFromApi with it. */
export function withCache(fetch: typeof fetchFromApi) {
  return async (url: string | URL, cache: CacheRef, { haveBody, ...options }: CachedFetchOptions = {}): Promise<CachedFetchResult> => {
    let entry = await getCacheEntry(cache);
    if (entry && haveBody && !(await haveBody())) {
      await deleteCacheEntry(cache);
      entry = null;
    }
    const result = await fetch(url, {
      ...options,
      storedETag: entry?.etag ?? undefined,
      storedLastModified: entry?.last_modified ?? undefined,
    });
    return {
      ...result,
      commit: async () => {
        if (result.notModified || !result.response.ok) return;
        // A row with no validator could never buy a 304; nothing to record.
        if (!result.eTag && !result.lastModified) return;
        await putCacheEntry(cache, { etag: result.eTag, lastModified: result.lastModified });
      },
    };
  };
}
